<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import CustomStore from 'devextreme/data/custom_store';
import type { LoadOptions } from 'devextreme/data';
import {
  DxDataGrid,
  DxColumn,
  DxScrolling,
  DxButton as DxGridButton,
} from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue/button';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';
import { DxForm, DxSimpleItem, DxRequiredRule } from 'devextreme-vue/form';
import { bottoGridPaging, showError, showSuccess } from '@botto/core';
import { trackerNodesApi, type TrackerNode } from '../../api';
import TrackerListLayout from '../../components/TrackerListLayout.vue';
import { formatDateTime } from '../../utils/format';
import { pageFromLoadOptions, resetGridPage, trackerGridProps } from '../../utils/gridUi';

const { paging, pager } = bottoGridPaging({ pageSize: 50 });
const grid = ref<InstanceType<typeof DxDataGrid> | null>(null);

const showModal = ref(false);
const editing = ref<TrackerNode | null>(null);
const saving = ref(false);
const formError = ref('');
const form = reactive({
  name: '',
  public_ip: '',
  base_url: '',
  status: 'active',
});

const statusItems = ['active', 'paused'];

const store = new CustomStore({
  key: 'id',
  loadMode: 'processed',
  async load(loadOptions: LoadOptions) {
    const { page, page_size } = pageFromLoadOptions(loadOptions, paging.pageSize);
    try {
      const res = await trackerNodesApi.listTrackerNodes({ page, page_size });
      return { data: res.items || [], totalCount: res.total || 0 };
    } catch (e) {
      showError(e);
      return { data: [], totalCount: 0 };
    }
  },
});

function refreshGrid() {
  resetGridPage(grid.value?.instance ?? null);
}

async function refresh() {
  try {
    await grid.value?.instance?.refresh();
  } catch (e) {
    showError(e);
  }
}

function openCreate() {
  editing.value = null;
  form.name = '';
  form.public_ip = '';
  form.base_url = '';
  form.status = 'active';
  formError.value = '';
  showModal.value = true;
}

function openEdit(n: TrackerNode) {
  editing.value = n;
  form.name = n.name;
  form.public_ip = n.public_ip || '';
  form.base_url = n.base_url || '';
  form.status = n.status || 'active';
  formError.value = '';
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

async function save() {
  saving.value = true;
  formError.value = '';
  try {
    const name = form.name.trim();
    if (!name) {
      formError.value = 'Имя обязательно';
      return;
    }
    const body = {
      name,
      public_ip: form.public_ip.trim(),
      base_url: form.base_url.trim() || null,
      status: form.status,
    };
    if (editing.value) {
      await trackerNodesApi.updateTrackerNode(editing.value.id, body);
      showSuccess('Нода обновлена');
    } else {
      await trackerNodesApi.createTrackerNode(body);
      showSuccess('Нода создана');
    }
    closeModal();
    refreshGrid();
  } catch (e) {
    formError.value = e instanceof Error ? e.message : 'Ошибка сохранения';
  } finally {
    saving.value = false;
  }
}

async function removeNode(n: TrackerNode) {
  if (n.name === 'Default') return;
  if (!window.confirm(`Удалить ноду «${n.name}»? Это нельзя отменить.`)) return;
  try {
    await trackerNodesApi.deleteTrackerNode(n.id);
    showSuccess('Нода удалена');
    await grid.value?.instance?.refresh();
  } catch (e) {
    showError(e);
  }
}

onMounted(() => {
  /* grid loads via CustomStore */
});
</script>

<template>
  <TrackerListLayout title="Edge-ноды">
    <template #actions>
      <DxButton icon="add" text="Добавить" type="default" styling-mode="contained" @click="openCreate" />
      <DxButton icon="refresh" text="Обновить" styling-mode="outlined" @click="refresh" />
    </template>

    <template #hint>
      Белый IP edge-сервера для DNS A-записи домена. Core API один; на edge — nginx с
      <code>X-Tracker-Node</code>. Ноду Default удалить нельзя; пустой IP → fallback на
      <code>SERVER_IP_OVERRIDE</code>.
    </template>

    <DxDataGrid
      ref="grid"
      :data-source="store"
      :remote-operations="{ paging: true, sorting: false, filtering: false }"
      v-bind="trackerGridProps"
      :paging="paging"
      :pager="pager"
      height="100%"
      width="100%"
    >
      <DxScrolling mode="standard" />

      <DxColumn data-field="id" caption="ID" width="80" />
      <DxColumn data-field="name" caption="Имя" />
      <DxColumn data-field="public_ip" caption="Public IP" css-class="mono" width="160" />
      <DxColumn
        data-field="base_url"
        caption="Base URL"
        css-class="mono"
        :calculate-cell-value="(r: TrackerNode) => r.base_url || '—'"
      />
      <DxColumn data-field="status" caption="Статус" width="110" />
      <DxColumn
        data-field="updated_at"
        caption="Обновлена"
        width="180"
        :calculate-cell-value="(row: TrackerNode) => formatDateTime(row.updated_at)"
      />

      <DxColumn type="buttons" :width="100" caption="Действия">
        <DxGridButton hint="Изменить" icon="edit" :on-click="(e: any) => openEdit(e.row.data)" />
        <DxGridButton hint="Удалить" icon="trash" :on-click="(e: any) => removeNode(e.row.data)" />
      </DxColumn>
    </DxDataGrid>

    <template #overlays>
      <DxPopup
        v-model:visible="showModal"
        :title="editing ? 'Редактировать ноду' : 'Добавить ноду'"
        :width="480"
        :height="'auto'"
        :show-close-button="true"
        @hiding="closeModal"
      >
        <template #content>
          <div v-if="formError" class="form-error">{{ formError }}</div>
          <DxForm :form-data="form" label-location="top">
            <DxSimpleItem
              data-field="name"
              editor-type="dxTextBox"
              :label="{ text: 'Имя' }"
              :editor-options="{ placeholder: 'MSK-1', disabled: editing?.name === 'Default' }"
            >
              <DxRequiredRule message="Обязательно" />
            </DxSimpleItem>
            <DxSimpleItem
              data-field="public_ip"
              editor-type="dxTextBox"
              :label="{ text: 'Public IPv4' }"
              :editor-options="{ placeholder: '203.0.113.10' }"
            />
            <DxSimpleItem
              data-field="base_url"
              editor-type="dxTextBox"
              :label="{ text: 'Base URL (опц.)' }"
              :editor-options="{ placeholder: 'https://t1.example.net' }"
            />
            <DxSimpleItem
              data-field="status"
              editor-type="dxSelectBox"
              :label="{ text: 'Статус' }"
              :editor-options="{ items: statusItems }"
            />
          </DxForm>
        </template>
        <DxToolbarItem widget="dxButton" toolbar="bottom" location="after" :options="{ text: 'Отмена', onClick: closeModal }" />
        <DxToolbarItem
          widget="dxButton"
          toolbar="bottom"
          location="after"
          :options="{ text: saving ? 'Сохранение…' : 'Сохранить', type: 'default', disabled: saving, onClick: save }"
        />
      </DxPopup>
    </template>
  </TrackerListLayout>
</template>

<style scoped>
.form-error {
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  background: #fdecea;
  color: #c62828;
  font-size: 0.875rem;
}

:deep(.mono) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.9em;
}
</style>
